import { skipToken } from "@reduxjs/toolkit/query";
import type {
  Automation,
  AutomationId,
  AutomationTemplateId,
} from "@repo/app-types/entities/automation";
import {
  automationIdSchema,
  AutomationTemplateIds,
} from "@repo/app-types/entities/automation";
import type {
  AutomationRunDb,
  AutomationRunId,
  AutomationRunStatus,
} from "@repo/app-types/entities/automationRun";
import {
  automationRunIdSchema,
  TERMINAL_AUTOMATION_RUN_STATUSES,
} from "@repo/app-types/entities/automationRun";
import { renderTeammateDisplayName } from "@repo/app-types/entities/teammate";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import {
  AutomationRunPreviewPill,
  formatRunTime,
} from "@repo/ui/automations/automationPresentation";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import { TextSkeleton } from "@repo/ui/Skeletons/skeletons";
import type { UIMessage } from "ai";
import {
  useCallback,
  useEffect,
  useMemo,
  type FC,
  type ReactNode,
} from "react";
import { Navigate, useParams } from "react-router";
import { useAutomationByIdQuery } from "../../api/entities/automation.js";
import {
  useAutomationRunByIdQuery,
  useAutomationRunThreadMessagesQuery,
  useCancelAutomationRunMutation,
} from "../../api/entities/automationRun.js";
import { useAllTeammatesQuery } from "../../api/entities/teammate.js";
import {
  getTagsInvalidatedByCreate,
  getTagsProvidedByEntity,
  TagType,
} from "../../api/helpers/tags.js";
import { api } from "../../api/redux.js";
import { PageBody, PageLayout } from "../../components/layout/pageLayout.js";
import {
  AutomationRunStreamProvider,
  useAutomationRunStatusLive,
  useAutomationRunStreamKillSwitch,
} from "../../features/automations/automationRunStreamProvider.js";
import { OrchestratorTranscript } from "../../features/automations/orchestratorTranscript.js";
import { RunInterpretationStep } from "../../features/automations/runInterpretationStep.js";
import { useAutomationRunMessagesLive } from "../../features/automations/useAutomationRunMessagesLive.js";
import { useTeammateAuthorization } from "../../helpers/teammateAuthorizationContext.js";
import { useAppDispatch } from "../../hooks/hook.js";

/**
 * The "Cancel run" affordance for a run that hasn't finished. Cancelling a
 * queued run settles it immediately; an active run winds down cooperatively
 * (the worker aborts its agents), so the button stays visible — and the
 * mutation pending/disabled — until the streamed run status turns terminal.
 */
const CancelRunButton: FC<{
  automationId: AutomationId;
  runId: AutomationRunId;
}> = ({ automationId, runId }) => {
  const [cancelRun, { isLoading, isSuccess }] =
    useCancelAutomationRunMutation();
  return (
    <Button
      variant={ButtonVariant.Warning}
      size={Size.SMALL}
      isLoading={isLoading}
      isDisabled={isLoading || isSuccess}
      onClick={() => void cancelRun({ automationId, automationRunId: runId })}
    >
      {isSuccess ? "Cancelling…" : "Cancel run"}
    </Button>
  );
};

export interface AgentRunConversationViewProps {
  status: AutomationRunStatus;
  isConversationLoading: boolean;
  /** True once the run's conversation has any assistant content to render. */
  hasConversation: boolean;
  /** The rendered transcript, supplied by the caller. */
  children: ReactNode;
}

/**
 * The presentational state machine around a run's conversation: a "waiting to
 * start" placeholder while pending, a loader or empty message before any
 * conversation exists, and otherwise the transcript itself (supplied as
 * children, so the styleguide can render static transcripts). Holds no
 * data-fetching; terminal-ness is derived from `status`, the single source of
 * truth.
 */
export const AgentRunConversationView: FC<AgentRunConversationViewProps> = (
  props,
) => {
  const { status, isConversationLoading, hasConversation, children } = props;

  if (status === "pending") {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader style={LoaderStyle.SPINNER} className="my-0" />
        <span>Waiting to start…</span>
      </div>
    );
  }

  if (!hasConversation) {
    if (status === "running") {
      return (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader style={LoaderStyle.SPINNER} className="my-0" />
          <span>Starting…</span>
        </div>
      );
    }

    if (isConversationLoading) {
      return <TextSkeleton />;
    }

    return (
      <p className="text-sm text-muted-foreground">
        This run did not produce a conversation.
      </p>
    );
  }

  return children;
};

/**
 * A run's body: the orchestrator agent's conversation, rendered as a chat
 * thread like any other — while the run is going we tail the orchestrator's
 * live thread stream; once it's terminal we read the persisted thread. Each
 * delegation tool call nests that manager's subagent thread (discovered from
 * the call's input, so a running subagent streams live).
 */
interface AgentRunConversationProps {
  playbookId: string | undefined;
  automationId: AutomationId;
  templateId: AutomationTemplateId;
  run: AutomationRunDb;
  /** The run's live-tracked status (see {@link AgentRunDetailLoaded}). */
  status: AutomationRunStatus;
  teammateNamesById: ReadonlyMap<string, string>;
}

const AgentRunConversation: FC<AgentRunConversationProps> = (props) => {
  const {
    playbookId,
    automationId,
    templateId,
    run,
    status,
    teammateNamesById,
  } = props;
  const isRunTerminal = TERMINAL_AUTOMATION_RUN_STATUSES.has(status);

  // A report saved mid-run (in the orchestrator's thread or any subagent's) is
  // a new row the results list doesn't have yet. Invalidate that list's cache
  // so it refetches the next time it's shown — the results view isn't mounted
  // alongside a live run, so there's nothing to refetch right now.
  const dispatch = useAppDispatch();
  const onArtifactSaved = useCallback(() => {
    dispatch(
      api.util.invalidateTags(
        getTagsInvalidatedByCreate(TagType.AutomationArtifact, {
          inLists: [{ filteredBy: [automationId] }],
        }),
      ),
    );
  }, [dispatch, automationId]);

  // The orchestrator's conversation is its own Mastra thread, keyed by the run
  // id. Live and persisted reads produce the same UIMessage shape. (The live
  // hook is a no-op on a finished run — there is no live stream to join.)
  const { messages: liveMessages } = useAutomationRunMessagesLive({
    threadId: run.id,
  });
  const { data: staticMessages, isLoading: areStaticMessagesLoading } =
    useAutomationRunThreadMessagesQuery(
      isRunTerminal
        ? { automationId, automationRunId: run.id, threadId: run.id }
        : skipToken,
    );
  const messages: readonly UIMessage[] = useMemo(
    () => (isRunTerminal ? (staticMessages ?? []) : liveMessages),
    [isRunTerminal, staticMessages, liveMessages],
  );

  const hasConversation = useMemo(
    () => messages.some((message) => message.role === "assistant"),
    [messages],
  );

  // A custom run writes `run.interpretation` after this page has already
  // fetched the run row, so a live viewer would never see it. Refetch the row
  // once the conversation starts (interpretation is done by then) or the run
  // ends.
  const shouldFetchInterpretation =
    templateId === AutomationTemplateIds.CUSTOM &&
    run.interpretation === null &&
    (hasConversation || isRunTerminal);
  useEffect(() => {
    if (shouldFetchInterpretation) {
      dispatch(
        api.util.invalidateTags(
          getTagsProvidedByEntity(TagType.AutomationRun, run.id),
        ),
      );
    }
  }, [shouldFetchInterpretation, dispatch, run.id]);

  return (
    <div className="flex flex-col gap-3">
      {run.interpretation ? (
        <RunInterpretationStep interpretation={run.interpretation} />
      ) : null}
      <AgentRunConversationView
        status={status}
        isConversationLoading={areStaticMessagesLoading}
        hasConversation={hasConversation}
      >
        <OrchestratorTranscript
          playbookId={playbookId}
          automationId={automationId}
          templateId={templateId}
          runId={run.id}
          isRunTerminal={isRunTerminal}
          messages={messages}
          isStreaming={status === "running"}
          teammateNamesById={teammateNamesById}
          onArtifactSaved={onArtifactSaved}
        />
      </AgentRunConversationView>
    </div>
  );
};

export interface AgentRunDetailPageViewProps {
  runTime: string;
  isPreview: boolean;
  /** Action rendered to the far right of the page title (e.g. Cancel run). */
  titleAction?: ReactNode;
  children: ReactNode;
}

/**
 * The run-detail page chrome — the run heading and a preview pill — around the
 * run's body (its conversation). Pure, so the styleguide can render the full
 * page in any state by supplying the body as children.
 */
export const AgentRunDetailPageView: FC<AgentRunDetailPageViewProps> = (
  props,
) => {
  const { runTime, isPreview, titleAction, children } = props;
  return (
    <PageLayout>
      <PageHeader
        width={PageWidth.Standard}
        eyebrow="Run"
        title={
          isPreview ? (
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate">{runTime}</span>
              <AutomationRunPreviewPill />
            </span>
          ) : (
            runTime
          )
        }
        actions={titleAction ?? undefined}
      />

      <PageBody width={PageWidth.Standard}>{children}</PageBody>
    </PageLayout>
  );
};

interface AgentRunDetailLoadedProps {
  playbookId: string | undefined;
  automation: Automation;
  run: AutomationRunDb;
  teammateNamesById: ReadonlyMap<string, string>;
}

/**
 * The loaded run page: owns the run's live-tracked status. The run's lifecycle
 * rides the multiplexed run stream as `data-run-status` parts, so the page
 * reacts to the run starting and finishing without polling the run record; we
 * fall back to the record's status until the stream reports one (e.g. a run
 * already terminal when the page loads, which has no live stream). The status
 * drives both the conversation's lifecycle states and the Cancel run title
 * action, which hides once the run is terminal.
 */
const AgentRunDetailLoaded: FC<AgentRunDetailLoadedProps> = (props) => {
  const { playbookId, automation, run, teammateNamesById } = props;
  // Reviewers can view runs but lack UPDATE on automations, so they can't
  // cancel one — the API rejects it. Hide the button rather than let it 403.
  const canCancelRun = useTeammateAuthorization().can(
    PermissionActions.UPDATE,
    PermissionSubjects.AUTOMATIONS,
  );
  const statusLive = useAutomationRunStatusLive();
  const effectiveStatus = statusLive.status ?? run.status;
  const isRunTerminal = TERMINAL_AUTOMATION_RUN_STATUSES.has(effectiveStatus);

  // Hard kill switch: once the run is terminal by any signal (streamed status,
  // refetched record), force-close the live connection instead of relying on
  // the server's sentinel to end it — e.g. a cancellation whose worker died
  // mid-wind-down would otherwise hold the stream to its duration cap.
  const killRunStream = useAutomationRunStreamKillSwitch();
  useEffect(() => {
    if (isRunTerminal) {
      killRunStream();
    }
  }, [isRunTerminal, killRunStream]);

  const runTime = formatRunTime(run.started_at ?? run.created_at);

  return (
    <AgentRunDetailPageView
      runTime={runTime}
      isPreview={run.is_preview}
      titleAction={
        isRunTerminal || !canCancelRun ? null : (
          <CancelRunButton automationId={automation.id} runId={run.id} />
        )
      }
    >
      <AgentRunConversation
        playbookId={playbookId}
        automationId={automation.id}
        templateId={automation.template_id}
        run={run}
        status={effectiveStatus}
        teammateNamesById={teammateNamesById}
      />
    </AgentRunDetailPageView>
  );
};

/**
 * Detail page for a single agent run. Shows the run's metadata and the
 * orchestrator agent's conversation, with each subagent's thread nested inside
 * its delegation tool call.
 */
export const AgentRunDetailPage: FC = () => {
  const { playbookId, automationId, runId } = useParams();

  const parsedAutomationId = automationId
    ? automationIdSchema.safeParse(automationId)
    : undefined;
  const parsedRunId = runId
    ? automationRunIdSchema.safeParse(runId)
    : undefined;

  const { data: automation, isLoading: isAutomationLoading } =
    useAutomationByIdQuery(
      parsedAutomationId?.success ? parsedAutomationId.data : skipToken,
    );
  // Fetched once for the run's metadata and its initial status; the live stream
  // (read inside AgentRunDetailLoaded) drives status changes from here on, so
  // there's no need to poll.
  const { data: run, isLoading: isRunLoading } = useAutomationRunByIdQuery(
    parsedAutomationId?.success && parsedRunId?.success
      ? {
          automationId: parsedAutomationId.data,
          automationRunId: parsedRunId.data,
        }
      : skipToken,
  );

  const { data: teammates } = useAllTeammatesQuery();

  const teammateNamesById = useMemo(
    () =>
      new Map<string, string>(
        (teammates ?? []).map((teammate) => [
          teammate.id,
          renderTeammateDisplayName(teammate),
        ]),
      ),
    [teammates],
  );

  if (!parsedAutomationId?.success || !parsedRunId?.success) {
    return <Navigate to=".." replace />;
  }

  if (isAutomationLoading || isRunLoading) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <Loader style={LoaderStyle.ZOOMIES} />
      </div>
    );
  }

  if (!automation || !run) {
    return <Navigate to=".." replace />;
  }

  return (
    // The provider owns the run page's single live-stream connection; the
    // status and thread hooks below it demultiplex by thread id. A run already
    // terminal on load has no live stream to join.
    <AutomationRunStreamProvider
      automationId={automation.id}
      runId={run.id}
      isEnabled={!TERMINAL_AUTOMATION_RUN_STATUSES.has(run.status)}
    >
      <AgentRunDetailLoaded
        playbookId={playbookId}
        automation={automation}
        run={run}
        teammateNamesById={teammateNamesById}
      />
    </AutomationRunStreamProvider>
  );
};
